import { applyMiddleware, createStore, Reducer } from "redux";
import thunk from "redux-thunk";

import { MAX_WIDTH } from "../../../Controls/constants";
import { PanelType } from "../../../Controls/FormParts/Panel/PanelTypes";
import { unitReducer } from "../../../Controls/FormParts/Unit/Store/unitReducer";
import { crossFitTableMiddleware } from "../../../Controls/MultiControls/CrossfitTable/Store/crossFitTableMiddleware";
import { crossFitTableReducer } from "../../../Controls/MultiControls/CrossfitTable/Store/crossFitTableReducer";
import { getCandyHub } from "../../../Forms/Sentry/InitializeSentry";
import { FormSettings } from "../../../Forms/Types/FormSettings";
import { attachmentsReducer, defaultAttachmentsState } from "../../Externals/Attachments/Store/AttachmentsReducer";
import { IForm } from "../../Form/IForm";
import { tooltipReducer } from "../../TooltipsResolveReducer";
import { defaultErrorLogState, errorLogModelReducer, errorLogReducer } from "../ErrorManager/Store/ErrorLogReducer";
import { customErrorLogTooltipReducer } from "../../../Controls/FormParts/EmbeddedLog/ErrorList/ErrorLogTooltip/CustomErrorLogTooltipReducer";
import { isBrowser } from "../../../Helpers/BrowserHelpers";
import { importEmployeesReducer } from "../../../StaffServiceIntegration/Employees/State/ImportEmployeesReducer";
import {
    CaptionsViewMode,
    captionsViewModeKey,
    HintsViewMode,
    hintsViewModeKey,
} from "../../../Controls/Internals/ViewModeSelector/ViewModes";
import { viewModesReducer } from "../../../Controls/Internals/ViewModeSelector/Store/ViewModesReducer";
import { requisitesReducer } from "../../Externals/Requisites/Store/RequisitesReducer";
import { featureFlagsReducer } from "../../Externals/FeatureFlags/Store/FeatureFlagsReducer";
import { ModalStateReducer } from "../ModalManager/ModalStateReducer";
import { getPanelStateToLocalStorage } from "../../ActionCreators/PanelTypeStroing";
import { UnitIdSet } from "../../../Controls/FormParts/Unit/UnitId/UnitIdImmutableSet";
import { FeatureFlags } from "../../../Forms/Types/FeatureFlags";
import { employerDocumentsReducer } from "../../../StaffServiceIntegration/Documents/State/EmployerDocumentsReducer";
import { tabInfosReducer } from "../../../Controls/MultiControls/Tabs/Store/TabInfosReducer";
import { employeeRelativesReducer } from "../../../StaffServiceIntegration/Relatives/State/EmployeeRelativesReducer";
import { formVisualStateReducer } from "../../FormVisualStateReducer";
import { instanceNumberReducer } from "../../../Controls/MultiControls/InstanceNumber/Store/instanceNumberReducer";
import { formSettingsReducer } from "../../FormSettingsReducer";
import { contractorsReducer } from "../../../ContractorsServiceIntegration/State/ContractorsReducer";

import { FormStore } from "./CandyStore";
import { FullFormAction } from "./FullFormAction";
import { FullFormState } from "./FullFormState";
import { aggregateValuesMiddleware } from "./Middleware/aggregateValuesMiddleware";
import { FormReducer } from "./ReducerTypes";
import { composeEnchancers } from "./ReduxDevToolsExtensions";
import { sentryBreadcrumbsMiddleware } from "./SentryBreadcrumbsMiddleware";
import { getFromLocalStorage } from "./LocalStorageUtils";
import { modelChangeHookMiddleware } from "./Middleware/modelChangeHookMiddleware";

function createReducer(modelReducer: FormReducer): FormReducer {
    return (state: FullFormState, action: FullFormAction): FullFormState => {
        let nextState = state;

        nextState = crossFitTableReducer(nextState, action);
        nextState = unitReducer(nextState, action);
        nextState = ModalStateReducer(nextState, action);
        nextState = modelReducer(nextState, action);
        nextState = viewModesReducer(nextState, action);

        nextState = {
            ...nextState,
            attachments: attachmentsReducer(state.attachments, state, action),
            requisites: requisitesReducer(state.requisites, action),
            featureFlags: featureFlagsReducer(state.featureFlags, action),
            tabInfos: tabInfosReducer(state.tabInfos, action),
            errorLog: errorLogReducer(state.errorLog, action),
            customErrorLogTooltip: customErrorLogTooltipReducer(state.customErrorLogTooltip, action),
            importEmployees: importEmployeesReducer(state.importEmployees, action),
            contractors: contractorsReducer(state.contractors, action),
            employerDocuments: employerDocumentsReducer(state.employerDocuments, action),
            employeeRelatives: employeeRelativesReducer(state.employeeRelatives, action),
            formVisualState: formVisualStateReducer(state.formVisualState, action),
            settings: formSettingsReducer(state.settings, action),
        };
        nextState = errorLogModelReducer(nextState, action);
        nextState = tooltipReducer(nextState, action);
        nextState = instanceNumberReducer(nextState, action);

        return nextState;
    };
}

function getInitialState(formSettings: FormSettings, featureFlags: FeatureFlags): FullFormState {
    const panelState: PanelType =
        getPanelStateToLocalStorage(formSettings) ??
        (isBrowser && document.body.offsetWidth > MAX_WIDTH ? "pin" : "hide");
    const captionsViewMode = getFromLocalStorage<CaptionsViewMode>(captionsViewModeKey, CaptionsViewMode.Long);
    const hintsViewMode = getFromLocalStorage<HintsViewMode>(hintsViewModeKey, HintsViewMode.Show);

    return {
        attachments: defaultAttachmentsState,
        units: {},
        viewModel: {},
        errorLog: defaultErrorLogState,
        settings: formSettings,
        featureFlags: featureFlags,
        modalUnits: [],
        emptyUnits: UnitIdSet.create(),
        viewModes: {
            captions: captionsViewMode,
            hints: hintsViewMode,
        },
        tableInfos: {},
        tabInfos: {
            currentInstance: {},
            currentTabInstanceIndex: {},
        },
        formVisualState: {
            savingStatus: "success",
            panelActiveTab: "menu",
            isWidePanel: false,
            panelState: panelState,
        },
    };
}

export function createFormStore(form: IForm, modelReducer: FormReducer, state: Partial<FullFormState> = {}): FormStore {
    // @ts-ignore
    const reducer: Reducer<FullFormState, FullFormAction> = createReducer(modelReducer);
    // @ts-ignore
    return createStore(
        reducer,
        { ...getInitialState(form.settings, form.featureFlags), ...state },
        composeEnchancers(
            applyMiddleware(
                crossFitTableMiddleware(),
                thunk.withExtraArgument(form),
                aggregateValuesMiddleware(form),
                modelChangeHookMiddleware(form),
                sentryBreadcrumbsMiddleware(getCandyHub())
            )
        )
    );
}
